import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { useContent } from "@/content/ContentContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useSwipe } from "@/hooks/useSwipe";

const INTERVAL = 7000;
const EASE = [0.22, 1, 0.36, 1];

const lineTransition = (i) => ({ duration: 1.1, delay: 0.45 + i * 0.16, ease: EASE });

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

const Hero = () => {
  const { t } = useLang();
  const { images, waLink } = useContent();
  const heroSlides = images.hero;
  const [index, setIndex] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 200]);
  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0]);

  const go = useCallback((i) => setIndex(i), []);
  const step = useCallback(
    (dir) => setIndex((i) => (i + dir + heroSlides.length) % heroSlides.length),
    [heroSlides.length]
  );
  const swipe = useSwipe(() => step(1), () => step(-1));

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), INTERVAL);
    return () => clearInterval(id);
  }, [index, heroSlides.length]);

  const slide = heroSlides[index % heroSlides.length];

  return (
    <section
      className="relative min-h-[100svh] overflow-hidden bg-anthracite touch-pan-y"
      data-testid="hero-section"
      {...swipe}
    >
      <motion.div style={{ y }} className="absolute inset-0" data-testid="hero-slider">
        <AnimatePresence initial={false}>
          <motion.img
            key={slide}
            src={slide}
            alt={t.hero.slides?.[index] || "U Magazin"}
            data-edit-list="hero"
            data-edit-index={index}
            fetchPriority={index === 0 ? "high" : "auto"}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.985, transition: { duration: 2.6, ease: [0.45, 0, 0.55, 1] } }}
            transition={{
              opacity: { duration: 2.6, ease: [0.45, 0, 0.55, 1] },
              scale: { duration: INTERVAL / 1000 + 3, ease: "linear" },
            }}
            className={`absolute inset-0 w-full h-full object-cover ${index === 3 ? "object-[50%_62%]" : "object-center"}`}
            data-testid={`hero-slide-${index}`}
          />
        </AnimatePresence>
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-b from-anthracite/65 via-anthracite/45 to-anthracite/90"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-r from-anthracite/60 via-anthracite/20 to-transparent"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_22%_62%,rgba(39,37,34,0.55),transparent_70%)]"
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 min-h-[100svh] max-w-7xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-32 pt-32"
      >
        <motion.p {...fade(0.25)} className="eyebrow text-ivory/80 mb-8 md:mb-10 hero-shadow" data-testid="hero-eyebrow">
          {t.hero.eyebrow}
        </motion.p>

        <h1
          className="font-serif text-ivory text-[2.9rem] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[6.5rem] tracking-tight hero-shadow"
          data-testid="hero-title"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" initial={{ y: "115%" }} animate={{ y: 0 }} transition={lineTransition(0)}>
              {t.hero.titleA}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-3">
            <motion.span
              className="block italic text-sand"
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={lineTransition(1)}
            >
              {t.hero.titleB}
            </motion.span>
          </span>
        </h1>

        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-10 md:gap-8 items-end">
          <motion.p
            {...fade(1.05)}
            className="md:col-span-6 lg:col-span-5 max-w-xl text-ivory/85 text-base md:text-lg font-light leading-relaxed hero-shadow"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            {...fade(1.25)}
            className="md:col-span-6 lg:col-span-7 flex flex-wrap items-center md:justify-end gap-4"
          >
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="btn-light"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t.hero.ctaPrimary}
            </a>
            <Link to="/menu" data-testid="hero-menu-button" className="btn-ghost">
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>
        </div>

        <motion.div
          {...fade(1.6)}
          className="mt-14 md:mt-20 pt-6 border-t border-ivory/15 grid grid-cols-2 md:grid-cols-3 items-center gap-4 text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-ivory/50"
        >
          <span aria-hidden="true">Via Roma 18 · Carloforte</span>

          <div className="order-last col-span-2 md:order-none md:col-span-1 flex items-center justify-center gap-5 pt-2 md:pt-0" data-testid="hero-slider-controls">
            <div className="flex items-center gap-2">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  aria-label={t.hero.slides?.[i] || `${i + 1}`}
                  data-testid={`hero-slider-dot-${i}`}
                  className="relative h-px w-6 md:w-8 bg-ivory/25 overflow-hidden"
                >
                  {i === index && (
                    <motion.span
                      key={index}
                      className="absolute inset-y-0 left-0 w-full bg-sand origin-left"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: INTERVAL / 1000, ease: "linear" }}
                    />
                  )}
                  {i < index && <span className="absolute inset-0 bg-ivory/60" />}
                </button>
              ))}
            </div>
            <span className="tabular-nums whitespace-nowrap text-ivory/70" data-testid="hero-slider-counter">
              {String(index + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
            </span>
          </div>

          <span className="flex items-center justify-end gap-3" aria-hidden="true">
            {t.hero.scroll}
            <motion.span
              animate={{ scaleX: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="block w-10 h-px bg-ivory/50 origin-left"
            />
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
